/**
 * Capability hook.
 *
 * Hides or disables controls the user cannot use. This is a COURTESY, never a
 * control: every hidden button corresponds to a server-side check that would
 * reject the request anyway.
 *
 * Code review rule: every useCan() has a matching require_permission().
 */

import type { Permission } from './catalog'

import { useActiveWorkspaceSlug } from '@/features/workspaces/active'
import { useMyAccess, useMyWorkspaces } from '@/features/workspaces/queries'


interface Access {
  permissions: ReadonlySet<string>
  role: string
  memberId: string | null
  isLoading: boolean
}

function useAccess(): Access {
  // The URL's workspace — or, on the reseller panel, the one last opened.
  const workspaceSlug = useActiveWorkspaceSlug()
  const { data: workspaces } = useMyWorkspaces()
  const workspaceId = workspaces?.find((w) => w.slug === workspaceSlug)?.id
  const { data, isLoading } = useMyAccess(workspaceId)

  return {
    permissions: new Set(data?.permissions ?? []),
    role: data?.role ?? '',
    memberId: data?.member_id ?? null,
    isLoading,
  }
}

/** True when the user holds EVERY listed permission. */
export function useCan(...required: Permission[]): boolean {
  const { permissions } = useAccess()
  return required.every((p) => permissions.has(p))
}

/** True when the user holds AT LEAST ONE of the listed permissions. */
export function useCanAny(...required: Permission[]): boolean {
  const { permissions } = useAccess()
  return required.some((p) => permissions.has(p))
}

export function useRole(): string {
  return useAccess().role
}

export function useMemberId(): string | null {
  return useAccess().memberId
}

/** Loading state, so a screen can avoid flashing a "restricted" message. */
export function useAccessLoading(): boolean {
  return useAccess().isLoading
}
